import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ModeToggle } from "@/components/mode-toggle";

export function PageHeader({
  title,
  eyebrow,
  actions,
  className,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-background/85 px-4 backdrop-blur-md",
        className,
      )}
    >
      <SidebarTrigger className="size-8 shrink-0 rounded-lg md:hidden" />
      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="truncate text-sm font-semibold">{title}</h1>
        {eyebrow ? (
          <span className="hidden truncate text-xs text-muted-foreground sm:inline" data-numeric>
            {eyebrow}
          </span>
        ) : null}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        {actions}
        <ModeToggle />
      </div>
    </header>
  );
}

export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("w-full px-6 py-8 md:px-8", className)}>{children}</div>;
}

export function SectionTitle({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 pb-6">
      <div className="min-w-0 space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        {description ? (
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
